import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'light'

type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonStyleOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

interface ButtonLinkProps extends ComponentProps<typeof Link> {
    variant?: ButtonVariant
    size?: ButtonSize
}

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_1px_2px_rgba(35,34,30,0.2)] hover:bg-olive-deep hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_10px_24px_-10px_rgba(68,71,47,0.7)]',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-[#dcd8ca]',
    light: 'border border-border bg-paper text-foreground shadow-[0_1px_2px_rgba(35,34,30,0.05)] hover:bg-white hover:shadow-[0_10px_24px_-14px_rgba(35,34,30,0.35)]'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-[42px] px-4 text-[15px]',
    md: 'h-12 px-5 text-[15px]',
    lg: 'h-14 px-6 text-[17px]'
}

export const buttonStyles = ({ variant = 'primary', size = 'md', className }: ButtonStyleOptions = {}): string => {
    return cn(
        'group inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap select-none transition-[background-color,color,box-shadow,translate] duration-300 ease-out hover:-translate-y-0.5 active:translate-y-px motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-70',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

export const ButtonLink: FC<ButtonLinkProps> = ({ variant, size, className, ...props }): ReactNode => {
    return (
        <Link className={buttonStyles({ variant, size, className })} {...props} />
    )
}
